/**
 * УВЕДОМЛЕНИЯ КОМНАТЫ — серверные push-правила (глобальный scope, kind=room/override).
 *
 * ЗАЧЕМ СЕРВЕРНЫЕ ПРАВИЛА, а не локальный флаг: mute, сделанный здесь, обязан
 * действовать и в Element, и на телефоне — иначе «приглушил в ALG, а телефон
 * продолжает пищать» будет выглядеть багом.
 *
 * МОДЕЛЬ (ровно три состояния, как в Telegram):
 *  - "all"      — приходят все уведомления (нет наших правил);
 *  - "mentions" — обычные сообщения молчат, упоминания пушатся.
 *      Реализация: правило kind=room, rule_id=<roomId>, действия [dont_notify].
 *      Оно подавляет .m.rule.message, но НЕ подавляет override-правила с
 *      большим приоритетом (.m.rule.is_user_mention, .m.rule.roomnotif) —
 *      то есть ровно «только упоминания»;
 *  - "muted"    — молчит ВСЁ, включая упоминания.
 *      Реализация: override-правило с event_match по room_id и действиями
 *      [dont_notify]. Override выше любых других kind, поэтому глушит и
 *      упоминания.
 *
 * СОВМЕСТИМОСТЬ С ELEMENT — критично, поэтому формат правил повторяет
 * matrix-react-sdk/src/RoomNotifs.ts ОДИН В ОДИН (источник сверен по
 * ветке develop, см. ссылку внизу файла):
 *  - mute-override пишется с rule_id === roomId (НЕ генерированный id):
 *    комментарий Element дословно — «prevents duplicate copies of the rule»;
 *  - чужой mute распознаём по ФОРМЕ правила, а не по id: ровно одно условие
 *    event_match key=room_id pattern=<эта комната> И действия-пустышки
 *    ([] или [dont_notify]). Тогда mute, поставленный в Element, виден здесь,
 *    и наоборот — клиенты не ссорятся о том, чья это настройка;
 *  - mentions-only распознаём как включённое room-правило с rule_id == roomId
 *    и dont_notify — та же форма, что пишет Element.
 *
 * ОДНОВРЕМЕННО может существовать только одно из двух правил: смена режима
 * сначала удаляет оба, потом добавляет нужное. Иначе «mentions»+остаточный
 * override дал бы полный mute.
 *
 * ИСТОЧНИК ПРАВИЛ: client.pushRules обновляется событием ClientEvent.Sync
 * (SDK обрабатывает pushrules из /sync сам, см. sync.ts). Хук useRoomPushMode
 * подписывается на Sync и перечитывает — отдельного события «push rules
 * changed» в SDK нет.
 *
 * ВЕРСИЯ API (сверено по matrix-js-sdk v42.3.0):
 *  - PushRuleKind.Override = "override", PushRuleKind.RoomSpecific = "room",
 *    PushRuleActionName.DontNotify = "dont_notify", ConditionKind.EventMatch =
 *    "event_match" (src/@types/PushRules.ts);
 *  - сигнатуры addPushRule(scope, kind, ruleId, body) / deletePushRule(scope,
 *    kind, ruleId) — по докам класса MatrixClient (matrix-org.github.io/
 *    matrix-js-sdk, разделы addPushRule/deletePushRule) и по реальному коду
 *    вызовов в matrix-react-sdk/src/RoomNotifs.ts:
 *    https://raw.githubusercontent.com/matrix-org/matrix-react-sdk/develop/src/RoomNotifs.ts
 */
import {
  ClientEvent,
  ConditionKind,
  PushRuleActionName,
  PushRuleKind,
  type IPushRule,
  type MatrixClient,
} from "matrix-js-sdk";
import { useEffect, useState } from "react";

/** Режим уведомлений комнаты, как его видит человек на странице комнаты. */
export type RoomPushMode = "all" | "mentions" | "muted";

/** Scope всегда "global": device-scope правила мы не создаём (их ведут пушеры). */
const SCOPE = "global";

/**
 * Правило глушит уведомления? Дословная копия isMuteRule из RoomNotifs.ts:
 * DontNotify эквивалентен пустому массиву действий.
 */
function isMuteActions(rule: IPushRule): boolean {
  return (
    rule.actions.length === 0 ||
    (rule.actions.length === 1 && rule.actions[0] === PushRuleActionName.DontNotify)
  );
}

/**
 * Это mute-override ИМЕННО для нашей комнаты? Форма — как в Element:
 * ровно одно условие event_match по room_id с pattern == roomId И
 * действия-пустышки. Правило обязано быть включённым.
 */
function isMuteOverrideFor(rule: IPushRule, roomId: string): boolean {
  if (!rule.enabled) return false;
  const cond = rule.conditions?.length === 1 ? rule.conditions[0] : undefined;
  if (!cond) return false;
  if (cond.kind !== ConditionKind.EventMatch || cond.key !== "room_id") return false;
  if (cond.pattern !== roomId) return false;
  return isMuteActions(rule);
}

/** Включённый room-rule этой комнаты, глушащий обычные сообщения (= «только упоминания»). */
function isMentionsRoomRule(rule: IPushRule | undefined, roomId: string): boolean {
  if (!rule || !rule.enabled) return false;
  if (rule.rule_id !== roomId) return false;
  return isMuteActions(rule);
}

/**
 * Текущий режим комнаты.
 *
 * Порядок проверки ВАЖЕН (тот же, что у Element в getRoomNotifsState):
 * сначала mute-override (сильнейший, глушит и упоминания), потом room-rule.
 */
export function roomPushMode(client: MatrixClient, roomId: string): RoomPushMode {
  const overrides = client.pushRules?.global?.[PushRuleKind.Override] ?? [];
  if (overrides.some((r) => isMuteOverrideFor(r, roomId))) return "muted";

  const roomRules = client.pushRules?.global?.[PushRuleKind.RoomSpecific] ?? [];
  if (roomRules.some((r) => isMentionsRoomRule(r, roomId))) return "mentions";

  return "all";
}

/**
 * Установить режим. Одна запись на сервер за вызов плюс до двух удалений
 * старых правил. Ошибки не прячем: вызывающий UI обязан показать humanError,
 * иначе переключатель «вернётся обратно» молча.
 */
export async function setRoomPushMode(
  client: MatrixClient,
  roomId: string,
  mode: RoomPushMode,
): Promise<void> {
  // Удаляем ОБА возможных правила. deletePushRule на несуществующее правило
  // сервер отвечает 404 — это не ошибка для нас, глотаем именно её и только её.
  const drop = async (kind: PushRuleKind, ruleId: string) => {
    try {
      await client.deletePushRule(SCOPE, kind, ruleId);
    } catch (e) {
      const err = e as { errcode?: string; httpStatus?: number };
      if (err?.errcode !== "M_NOT_FOUND" && err?.httpStatus !== 404) throw e;
    }
  };

  if (mode === "muted") {
    // Element делает так же (setRoomNotifsStateMuted): удалить room-rule,
    // затем записать override с rule_id === roomId.
    await drop(PushRuleKind.RoomSpecific, roomId);
    await client.addPushRule(SCOPE, PushRuleKind.Override, roomId, {
      conditions: [
        { kind: ConditionKind.EventMatch, key: "room_id", pattern: roomId },
      ],
      actions: [PushRuleActionName.DontNotify],
    });
    return;
  }

  // unmute-путь: удалить mute-override (все подходящие по форме — вдруг их
  // оказалось несколько от разных клиентов), затем по режиму room-rule.
  const muteRuleIds = (client.pushRules?.global?.[PushRuleKind.Override] ?? [])
    .filter((r) => isMuteOverrideFor(r, roomId))
    .map((r) => r.rule_id);
  // Наш собственный формат — rule_id === roomId; удаляем и его на случай,
  // если правило выключено (isMuteOverrideFor его не увидит) или устарело.
  if (!muteRuleIds.includes(roomId)) muteRuleIds.push(roomId);
  for (const id of muteRuleIds) {
    await drop(PushRuleKind.Override, id);
  }

  if (mode === "mentions") {
    // kind=room: подавляет .m.rule.message (и encrypted), но НЕ override
    // (упоминания). Условий нет: room-правила матчатся по rule_id == roomId.
    await client.addPushRule(SCOPE, PushRuleKind.RoomSpecific, roomId, {
      actions: [PushRuleActionName.DontNotify],
    });
  }
  // mode === "all": правил нет, добавлять нечего.
}

/**
 * React-хук: режим комнаты, перечитываемый на каждый sync.
 *
 * Почему Sync, а не AccountData: pushRules — не account data, SDK обновляет
 * client.pushRules при обработке /sync и отдельного события не эмитит
 * (проверено по sync.ts: pushRules присваиваются в процессе sync). После
 * setRoomPushMode следующий sync обновит client.pushRules, и панель
 * перерисуется сама; до него хук вернёт старое значение — UI поэтому
 * показывает busy на время запроса и держит локальное эхо выбора.
 */
export function useRoomPushMode(client: MatrixClient, roomId: string): RoomPushMode {
  const [mode, setMode] = useState<RoomPushMode>(() => roomPushMode(client, roomId));
  useEffect(() => {
    const bump = () => setMode(roomPushMode(client, roomId));
    bump();
    client.on(ClientEvent.Sync, bump);
    return () => {
      client.off(ClientEvent.Sync, bump);
    };
  }, [client, roomId]);
  return mode;
}
